'use client';

// ── Figure registry ───────────────────────────────────────────────────────────
// Maps a Studio preset key (see ../presets.ts) to the hand-built component.
// Each figure renders its own white FigureFrame and owns its example numbers:
// the page banner tells the reader every value is illustrative.

import { GroqImage } from '@/helpers/Image';
import type { GROQImage } from '@/sanity/lib/definitions';
import { FigureFrame } from '../primitives';
import type { FigurePreset } from '../presets';
import { FIGURES } from './registry';

function ImageFigure({ image, imageMobile }: { image?: GROQImage; imageMobile?: GROQImage }) {
  if (!image) return null;
  return (
    <FigureFrame padding="p-[2rem] lg:p-[3rem]">
      <GroqImage
        src={image}
        alt={image.name ?? ''}
        quality={100}
        className={imageMobile ? 'hidden w-full lg:block' : 'w-full'}
        style={{ width: '100%', height: 'auto' }}
      />
      {imageMobile && (
        <GroqImage
          src={imageMobile}
          alt={imageMobile.name ?? ''}
          quality={100}
          className="w-full lg:hidden"
          style={{ width: '100%', height: 'auto' }}
        />
      )}
    </FigureFrame>
  );
}

function MissingFigure({ preset }: { preset?: string }) {
  if (process.env.NODE_ENV === 'production') return null;
  return (
    <FigureFrame className="border border-dashed border-black/20">
      <p className="mb-0 font-geist-mono text-[1.75rem] text-black/40">figure preset “{preset}” is not built yet</p>
    </FigureFrame>
  );
}

export function Figure({
  preset,
  image,
  imageMobile,
}: {
  preset?: string;
  image?: GROQImage;
  imageMobile?: GROQImage;
}) {
  if (!preset || preset === 'image') return <ImageFigure image={image} imageMobile={imageMobile} />;
  const Component = FIGURES[preset as FigurePreset];
  if (!Component) return <MissingFigure preset={preset} />;
  return <Component />;
}
